import { useState, useEffect, useCallback } from 'react';
import { FileText, Download, Image, Link2, Mail, MapPin, Music2, Instagram, Upload, X, Save, Palette, Crown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_PLATFORMS = ['instagram', 'tiktok', 'soundcloud', 'spotify', 'youtube', 'twitter'];

export const PressKitBuilder = ({ user }) => {
  const [kit, setKit] = useState(null);
  const [limits, setLimits] = useState({});
  const [tier, setTier] = useState('starter');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // Form state
  const [djName, setDjName] = useState('');
  const [bio, setBio] = useState('');
  const [genres, setGenres] = useState([]);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [mixLinks, setMixLinks] = useState(['', '', '']);
  const [socialLinks, setSocialLinks] = useState({});
  const [accentColor, setAccentColor] = useState('#D4AF37');

  const fetchPressKit = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/press-kit`);
      const data = response.data;
      
      setKit(data.kit);
      setLimits(data.limits);
      setTier(data.tier);
      
      // Populate form
      const k = data.kit;
      setDjName(k.dj_name || '');
      setBio(k.bio || '');
      setGenres(k.genres || []);
      setCity(k.city || '');
      setCountry(k.country || '');
      setContactEmail(k.contact_email || '');
      setMixLinks([...(k.mix_links || []), '', '', ''].slice(0, 3));
      setSocialLinks(k.social_links || {});
      setAccentColor(k.accent_color || '#D4AF37');
    } catch (error) {
      toast.error('Failed to load press kit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPressKit();
  }, [fetchPressKit]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/press-kit`, {
        dj_name: djName,
        bio: bio,
        genres: genres,
        city: city,
        country: country,
        contact_email: contactEmail,
        mix_links: mixLinks.filter(l => l.trim()),
        social_links: socialLinks,
        accent_color: accentColor
      });
      toast.success('Press kit saved!');
      fetchPressKit();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/press-kit/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${djName || 'DJ'}_Press_Kit.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Press kit downloaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download. Make sure to save your press kit first.');
    } finally {
      setDownloading(false);
    }
  };

  const addGenre = (genre) => {
    if (!genres.includes(genre) && genres.length < 5) {
      setGenres([...genres, genre]);
    }
  };

  const removeGenre = (genre) => {
    setGenres(genres.filter(g => g !== genre));
  };

  const AVAILABLE_GENRES = ['House', 'Tech House', 'Deep House', 'Techno', 'Trance', 'Drum & Bass', 'Dubstep', 'Hip Hop', 'R&B', 'Afrobeats', 'Reggaeton', 'Dancehall', 'UK Garage', 'Amapiano'];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="press-kit-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText size={24} style={{ color: '#D4AF37' }} />
            Press Kit Builder
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Create a professional DJ press kit to send to promoters and venues
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="rounded-full"
            style={{ background: '#D4AF37', color: '#000' }}
          >
            <Save size={16} className="mr-1" />
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            variant="outline"
            className="rounded-full"
            style={{ borderColor: '#22c55e', color: '#22c55e' }}
          >
            <Download size={16} className="mr-1" />
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </div>
      </div>

      {/* Tier Info Banner */}
      <div 
        className="p-4 rounded-xl flex items-center justify-between"
        style={{ 
          background: tier === 'elite' ? 'rgba(168, 85, 247, 0.1)' : tier === 'pro' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.05)',
          border: `1px solid ${tier === 'elite' ? 'rgba(168, 85, 247, 0.3)' : tier === 'pro' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`
        }}
      >
        <div className="flex items-center gap-3">
          <Crown size={20} style={{ color: tier === 'elite' ? '#a855f7' : tier === 'pro' ? '#3b82f6' : '#666' }} />
          <div>
            <p className="font-semibold text-sm">{tier.charAt(0).toUpperCase() + tier.slice(1)} Template</p>
            <p className="text-xs text-gray-400">
              {limits.max_photos} photo{limits.max_photos !== 1 ? 's' : ''} • {limits.max_mixes} mix link{limits.max_mixes !== 1 ? 's' : ''}
              {limits.custom_accent && ' • Custom accent color'}
              {limits.elite_badge && ' • Elite badge'}
            </p>
          </div>
        </div>
        {tier === 'starter' && (
          <span className="text-xs text-yellow-500">Upgrade for more features!</span>
        )}
      </div>

      {/* Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* DJ Name */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              <Music2 size={16} style={{ color: '#D4AF37' }} />
              DJ Name
            </Label>
            <Input
              value={djName}
              onChange={(e) => setDjName(e.target.value)}
              placeholder="Your DJ name"
              className="mt-1"
            />
          </div>

          {/* Bio */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              <FileText size={16} style={{ color: '#D4AF37' }} />
              Bio
            </Label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell your story... What makes you unique as a DJ?"
              rows={4}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">{bio.length}/500 characters</p>
          </div>

          {/* Genres */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              Genres (max 5)
            </Label>
            <div className="flex flex-wrap gap-2 mb-3">
              {genres.map((genre) => (
                <span 
                  key={genre}
                  className="flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                  style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}
                >
                  {genre}
                  <button onClick={() => removeGenre(genre)} className="ml-1 hover:text-red-500">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_GENRES.filter(g => !genres.includes(g)).slice(0, 8).map((genre) => (
                <button
                  key={genre}
                  onClick={() => addGenre(genre)}
                  className="px-2 py-1 rounded-full text-xs border border-gray-600 hover:border-[#D4AF37] transition-colors"
                  disabled={genres.length >= 5}
                >
                  + {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              <MapPin size={16} style={{ color: '#D4AF37' }} />
              Location
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
              />
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Country"
              />
            </div>
          </div>

          {/* Contact Email */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              <Mail size={16} style={{ color: '#D4AF37' }} />
              Booking Email
            </Label>
            <Input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="bookings@youremail.com"
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Mix Links */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              <Link2 size={16} style={{ color: '#D4AF37' }} />
              Mix Links ({mixLinks.filter(l => l.trim()).length}/{limits.max_mixes})
            </Label>
            <div className="space-y-2">
              {mixLinks.slice(0, limits.max_mixes).map((link, idx) => (
                <Input
                  key={idx}
                  value={link}
                  onChange={(e) => {
                    const newLinks = [...mixLinks];
                    newLinks[idx] = e.target.value;
                    setMixLinks(newLinks);
                  }}
                  placeholder={`Mix link ${idx + 1} (SoundCloud, Mixcloud, etc.)`}
                />
              ))}
            </div>
          </div>

          {/* Social Links */}
          <div className="p-4 rounded-xl" style={{ background: '#1a1a1a' }}>
            <Label className="flex items-center gap-2 mb-2">
              <Instagram size={16} style={{ color: '#D4AF37' }} />
              Social Links
            </Label>
            <div className="space-y-2">
              {SOCIAL_PLATFORMS.map((platform) => (
                <div key={platform} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-20 capitalize">{platform}</span>
                  <Input
                    value={socialLinks[platform] || ''}
                    onChange={(e) => setSocialLinks({ ...socialLinks, [platform]: e.target.value })}
                    placeholder={`@your${platform}handle`}
                    className="flex-1"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Accent Color (Elite only) */}
          {limits.custom_accent && (
            <div className="p-4 rounded-xl" style={{ background: '#1a1a1a', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
              <Label className="flex items-center gap-2 mb-2">
                <Palette size={16} style={{ color: '#a855f7' }} />
                Custom Accent Color
                <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">Elite</span>
              </Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="#D4AF37"
                  className="flex-1"
                />
              </div>
            </div>
          )}

          {/* Preview Card */}
          <div className="p-4 rounded-xl border" style={{ background: '#1a1a1a', borderColor: accentColor + '30' }}>
            <p className="text-xs text-gray-500 mb-3">Preview</p>
            <div 
              className="p-4 rounded-lg"
              style={{ background: '#1a1a2e' }}
            >
              <div 
                className="w-full h-12 rounded-t flex items-center justify-center mb-3"
                style={{ background: accentColor }}
              >
                <span className="font-bold text-lg" style={{ color: '#1a1a2e' }}>{djName || 'Your DJ Name'}</span>
              </div>
              <p className="text-xs text-gray-400 text-center mb-2">{city} {country && `, ${country}`}</p>
              <div className="flex flex-wrap gap-1 justify-center">
                {genres.slice(0, 3).map((g) => (
                  <span key={g} className="text-xs px-2 py-0.5 rounded-full" style={{ background: accentColor + '20', color: accentColor }}>
                    {g}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
